/**
 * Text helpers shared by the PDF renderer.
 *
 * jsPDF's built-in fonts are WinAnsi (cp1252) encoded, so anything outside that
 * repertoire — emoji, CJK, most symbols — renders as garbage rather than as a
 * missing glyph. Everything drawn into a PDF goes through `sanitizeForPdf`.
 */

/** Unicode code points that cp1252 maps into the 0x80-0x9F range. */
const CP1252_HIGH = new Set<number>([
  0x20ac, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021, 0x02c6, 0x2030, 0x0160, 0x2039, 0x0152,
  0x017d, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022, 0x2013, 0x2014, 0x02dc, 0x2122, 0x0161, 0x203a,
  0x0153, 0x017e, 0x0178,
]);

/** Common characters worth keeping in a readable form instead of dropping. */
const TRANSLITERATIONS: Record<string, string> = {
  '→': '->',
  '←': '<-',
  '⇒': '=>',
  '≤': '<=',
  '≥': '>=',
  '≠': '!=',
  '≈': '~',
  '−': '-',
  '‑': '-',
  '′': '\u0027',
  '″': '"',
  '●': '•',
  '▪': '•',
  '‣': '•',
  '⁃': '•',
  '✓': 'v',
  '✔': 'v',
  '✗': 'x',
  '✘': 'x',
};

const isRepresentable = (code: number): boolean =>
  (code >= 0x20 && code <= 0x7e) || (code >= 0xa0 && code <= 0xff) || CP1252_HIGH.has(code);

/**
 * Strips or transliterates anything the built-in PDF fonts cannot encode.
 * Tabs become spaces; newlines survive so callers can split on them.
 */
export const sanitizeForPdf = (input: string): string => {
  if (!input) return '';
  const nfc = typeof input.normalize === 'function' ? input.normalize('NFC') : input;
  // Non-breaking, thin, en/em and ideographic spaces all collapse to a plain
  // space so wrapping treats them as break opportunities.
  const normalized = nfc.replace(/[\u00a0\u1680\u2000-\u200a\u202f\u205f\u3000]/g, ' ');
  let out = '';

  for (const char of Array.from(normalized)) {
    if (char === '\n') {
      out += '\n';
      continue;
    }
    if (char === '\t') {
      out += '  ';
      continue;
    }

    const mapped = TRANSLITERATIONS[char];
    if (mapped !== undefined) {
      out += mapped;
      continue;
    }

    const code = char.codePointAt(0);
    if (code === undefined) continue;
    // Skip zero-width joiners, variation selectors and other invisible marks
    // outright so emoji sequences do not leave stray separators behind.
    if (code === 0x200d || (code >= 0xfe00 && code <= 0xfe0f)) continue;
    if (isRepresentable(code)) out += char;
  }

  return out;
};

/** Collapses whitespace and trims — for titles, labels and filenames. */
export const collapseWhitespace = (input: string): string => input.replace(/\s+/g, ' ').trim();

/**
 * Filename-safe slug, e.g. `NOx limits for Texas` -> `nox-limits-for-texas`.
 *
 * Long chat titles (often a whole question) are cut back to the last complete
 * word so filenames stay short and readable; a single oversized word is cut
 * hard rather than shrinking the name to almost nothing.
 */
export const slugify = (input: string, maxLength = 40): string => {
  const base = sanitizeForPdf(input)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (!base) return 'transcript';
  if (base.length <= maxLength) return base;

  const clipped = base.slice(0, maxLength).replace(/-+$/, '');
  const lastBoundary = clipped.lastIndexOf('-');
  if (lastBoundary >= Math.floor(maxLength * 0.6)) return clipped.slice(0, lastBoundary);
  return clipped;
};

/** `2026-09-18`, in local time. */
export const toFileDate = (date: Date): string => {
  const pad = (n: number) => `${n < 10 ? '0' : ''}${n}`;
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** `Sep 18, 2026` */
export const formatDisplayDate = (date: Date): string =>
  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export const truncate = (input: string, maxLength: number): string => {
  const text = collapseWhitespace(input);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
};
